import { formatDate, DATE_FORMATS } from '@frontend/mwm-ui'
import { useQueryClient } from '@tanstack/react-query'
import classNames from 'classnames'
import Tooltip from 'rc-tooltip'
import { useState } from 'react'
import ReactPlayer from 'react-player'

import type { ReactElement } from 'react'
import type { IVideoTag } from '~/types/video'

import { useAddVideoToFavouriteMutation, useDeleteVideoFromFavouriteMutation, keys } from '../queries'

import Avatar from '~/components/Avatar'
import Badge from '~/components/Badge'
import { VideoTagBadge } from '~/components/Badges'
import VideoCard from '~/components/VideoCard'
import { useUserDetailsQuery } from '~/queries/users'
import { VIDEO_CATEGORIES } from '~/types/video'
import dayjs from '~/utils/dayjs'
import { handleGenericError } from '~/utils/error'
import { displayToast } from '~/utils/toast'
import { parseDuration } from '~/utils/videos'

import './styles.scss'

const MAX_BADGES = 3

interface Props {
  createdAt: string
  createdBy: string
  description?: string
  duration?: number
  favourite?: boolean
  onClick: (videoUrl: string) => void
  onEdit: VoidFunction
  onFavouriteToggle?: VoidFunction
  tags: IVideoTag[]
  thumbnailUrl?: string
  title: string
  url: string
  videoId: string
}

const VideoThumbnail = ({
  createdAt,
  createdBy,
  description,
  duration,
  favourite,
  onClick,
  onEdit,
  onFavouriteToggle,
  tags,
  thumbnailUrl,
  title,
  url,
  videoId,
}: Props): ReactElement => {
  /** Local state */

  const queryClient = useQueryClient()

  const [videoDuration, setVideoDuration] = useState(duration || 0)

  const [isHover, setHover] = useState(false)

  const containerClassNames = classNames('video-thumbnail-container')

  const now = dayjs(Date.now())

  const dayDiff = now.diff(createdAt, 'days')

  /** Queries & Mutations */

  const { data: user } = useUserDetailsQuery(createdBy)

  const { mutateAsync: addVideoToFavouriteMutation } = useAddVideoToFavouriteMutation()

  const { mutateAsync: deleteVideoFromFavouriteMutation } = useDeleteVideoFromFavouriteMutation()

  /** Handlers */

  const handleHover = (value: boolean) => () => setHover(value)

  const handleAddFavourite = async () => {
    try {
      if (!favourite) {
        await addVideoToFavouriteMutation({ videoId })
        displayToast('Video successfully added to My list', 'success')
      } else {
        await deleteVideoFromFavouriteMutation({ videoId })
        displayToast('Video successfully deleted from My list', 'success')
      }

      queryClient.invalidateQueries({ queryKey: keys.user() })
      queryClient.invalidateQueries({ queryKey: keys.dynamic(VIDEO_CATEGORIES.MY_LIST) })
      onFavouriteToggle && onFavouriteToggle()
    } catch (e) {
      handleGenericError(e)
    }
  }

  const handlePreviewClick = () => {
    onClick(url)
    setHover(false)
  }

  return (
    <Tooltip
      align={{
        offset: [0, 0],
        points: ['cc', 'cc'],
        targetOffset: [0, 0],
      }}
      destroyTooltipOnHide
      mouseEnterDelay={1.2}
      overlay={
        <VideoCard
          description={description}
          favourite={favourite}
          onAddToFavourite={handleAddFavourite}
          onClick={handlePreviewClick}
          onEdit={onEdit}
          tags={tags}
          title={title}
          url={url}
        />
      }
      overlayClassName="video-card-tooltip-container"
      placement="right"
      trigger={['hover', 'click']}
    >
      <div className="video-thumbnail">
        <div
          className={containerClassNames}
          onClick={handlePreviewClick}
          onMouseEnter={handleHover(true)}
          onMouseLeave={handleHover(false)}
        >
          <div className="video-player-container">
            <ReactPlayer
              className="react-player"
              height="15.5rem"
              light={thumbnailUrl ?? false}
              onDuration={setVideoDuration}
              playIcon={<></>}
              playing={false}
              url={url}
              width="100%"
            />
            {isHover ? (
              <div className="video-hint">Hold the mouse over the video to start it</div>
            ) : (
              <div className="video-duration">{parseDuration(videoDuration)}</div>
            )}
          </div>
          <div className="video-details">
            {user ? <Avatar pictureUrl={user.picture} size="small" /> : null}
            <div className="video-details-text">
              <span className="video-title">{title}</span>
              {tags.length ? (
                <div className="video-tags">
                  {tags.slice(0, MAX_BADGES).map(({ name }, index) => (
                    <VideoTagBadge key={`tag-${index}`} value={name} />
                  ))}
                  {tags.slice(MAX_BADGES).length > 0 ? (
                    <Badge className="counter-badge" size="medium" value={`+${tags.length - MAX_BADGES}`} />
                  ) : null}
                </div>
              ) : null}
              <span className="video-date">
                {dayDiff === 0
                  ? 'Few hours ago'
                  : dayDiff > 3
                    ? formatDate(createdAt, DATE_FORMATS.DEFAULT_WITH_HOURS)
                    : `${dayDiff} day${dayDiff > 1 ? 's' : ''} ago`}
              </span>
            </div>
          </div>
        </div>
      </div>
    </Tooltip>
  )
}

export default VideoThumbnail
